import { useEffect } from "react";
import Lenis from "lenis";
import { useLocation } from "react-router-dom";

/**
 * Inertial smooth scrolling.
 *
 * Lenis owns the scroll position and nothing else — it never animates an
 * element. Framer Motion's scroll hooks read `window.scrollY`, which Lenis
 * keeps authoritative, so no bridging is required.
 *
 * Disabled entirely when the user has asked for reduced motion, and torn down
 * on unmount so native scrolling is always restored.
 */
export default function SmoothScroll({ children }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduced) return undefined;

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.6,
    });

    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    window.__lenis = lenis;

    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  // Route changes must land at the top instantly rather than easing there.
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, [pathname]);

  return <>{children}</>;
}
